import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDisclosure, useToast} from '@chakra-ui/react';
import {useNavigate} from 'react-router-dom';
import {useUser} from '../../contexts/UserContext';
import {
  canScheduleVideoCallLink,
  isSupportedVideoCallLink,
} from '../../common/videoCallLinks';
import {fetchConnectionDetails} from '../../livekit/LiveKitTokenService';
import {createBot} from '../../services/developerService';
import {
  requestPhoneCall,
  requestThreeWayCall,
} from '../../services/interpreterService';
import {toE164} from '../../services/encounterCallService';
import {saveProviderPhone} from '../../services/userProfileService';
import {LIVE_ENCOUNTER_WORKFLOW_TYPE} from '../../services/encounterUtils';
import {
  ENCOUNTER_ROUTE,
  PHONE_SESSION_ROUTE,
  VIDEO_SESSION_ROUTE,
} from '../../utils/encounterLaunch';
import {
  CREATE_SESSION_LAUNCH_ACTIONS,
  CREATE_SESSION_UNWIRED_TOAST_TITLE,
  buildCreateSessionDraft,
  buildCreateSessionLaunchIntent,
  formatCreateSessionPhoneNumber,
  firstValidationMessage,
  patientDialect as patientDialectFromItem,
  patientLanguage as patientLanguageFromItem,
  patientPhone as patientPhoneFromItem,
  shouldDefaultInterpreterOn,
  validateCreateSessionDraft,
} from './createSessionFormUtils';
import useCreateSessionLanguageFields from './useCreateSessionLanguageFields';
import useCreateSessionSchedule from './useCreateSessionSchedule';

export function useEncounterLauncher({
  patientItem = null,
  patientSkipped = false,
  patientChoiceRequired = false,
  fromPlanner = false,
  onScheduled = null,
}: Record<string, any> = {}) {
  const navigate = useNavigate();
  const {userData} = useUser();
  const toast = useToast();
  const scheduleDisclosure = useDisclosure();
  const [interpreterEnabled, setInterpreterEnabled] = useState(false);
  // Opt-in low-latency simultaneous interpretation (OpenAI gpt-realtime-translate),
  // versus the default turn-based cascaded interpreter. Only meaningful for a
  // live web encounter; ignored by every other launch path.
  const [simultaneousEnabled, setSimultaneousEnabled] = useState(false);
  const [locationType, setLocationType] = useState('inPerson');
  const [patientPhone, setPatientPhoneState] = useState('');
  // True once the provider has typed into the patient-number field. A
  // hand-entered number is the source of truth for who we actually dial, so it
  // must never be silently re-seeded from the patient's chart/EHR record (which
  // can hydrate or change asynchronously). Reset only when the patient itself
  // changes, or when the launch state is explicitly reset.
  const patientPhoneEditedRef = useRef(false);
  const [providerPhone, setProviderPhone] = useState('');
  // Phone flow: defaults OFF (the provider is not yet on a call with the
  // patient, so Opalite handles dialing). ON means the provider is already on
  // a call and only needs Opalite to dial them so they can merge.
  const [alreadyWithPatient, setAlreadyWithPatient] = useState(false);
  // When the patient is NOT already on the line, how the provider joins:
  // 'web' (default) starts the LiveKit room on this device and SIP-dials the
  // patient into it; 'phone' falls back to the legacy phone-based flow.
  const [phoneJoinMode, setPhoneJoinMode] = useState('web');
  const [videoCallLink, setVideoCallLink] = useState('');
  // Selected outbound caller ID (verified E.164) for web dial-out. '' = default.
  const [callerId, setCallerId] = useState('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const languageFields = useCreateSessionLanguageFields();
  const {dialect, language, resetLanguageFields, seedLanguageSelection} =
    languageFields;
  const patientPhoneSeed = useMemo(
    () => formatCreateSessionPhoneNumber(patientPhoneFromItem(patientItem)),
    [patientItem],
  );
  const patientSeedKey = patientItem?.id || '';
  const patientPhoneSeedRef = useRef({
    key: patientSeedKey,
    phone: patientPhoneSeed,
  });

  useEffect(() => {
    const previousSeed = patientPhoneSeedRef.current;
    const isNewPatient = previousSeed.key !== patientSeedKey;
    // A genuinely different patient clears the manual-edit guard so the new
    // patient's chart number seeds in.
    if (isNewPatient) {
      patientPhoneEditedRef.current = false;
    }
    setPatientPhoneState(current => {
      // Never overwrite a number the provider typed by hand — the field wins.
      if (patientPhoneEditedRef.current) return current;
      const shouldUsePatientSeed =
        isNewPatient ||
        locationType !== 'phone' ||
        !current ||
        current === previousSeed.phone;
      return shouldUsePatientSeed ? patientPhoneSeed : current;
    });
    patientPhoneSeedRef.current = {
      key: patientSeedKey,
      phone: patientPhoneSeed,
    };
  }, [locationType, patientPhoneSeed, patientSeedKey]);

  // UI-facing setter for the patient-number field: marks the value as manually
  // entered so chart/EHR seeding can never clobber it.
  const setPatientPhone = useCallback((value: any) => {
    patientPhoneEditedRef.current = true;
    setPatientPhoneState(value);
  }, []);

  // Default the interpreter on when the selected patient speaks a non-English
  // language the provider doesn't speak, and off otherwise. Keyed on the
  // patient id + the provider's spoken languages (stable string) so a manual
  // toggle within the same patient isn't clobbered, while a late-loading
  // provider profile still recomputes. Language seeding stays owned by the
  // consuming forms' own effects.
  const providerLanguagesKey = (userData?.spokenLanguages || []).join('|');
  useEffect(() => {
    setInterpreterEnabled(
      shouldDefaultInterpreterOn(patientItem, userData?.spokenLanguages),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientSeedKey, providerLanguagesKey]);

  const createSessionDraft = useMemo(
    () =>
      buildCreateSessionDraft({
        patientItem,
        patientSkipped,
        patientChoiceRequired,
        interpreterEnabled,
        language,
        dialect,
        locationType,
        patientPhone,
        providerPhone,
        alreadyWithPatient,
        phoneJoinMode,
        callerId,
        videoCallLink,
        fromPlanner,
        restorePatientItem: fromPlanner ? patientItem : null,
      }),
    [
      patientItem,
      patientSkipped,
      patientChoiceRequired,
      fromPlanner,
      interpreterEnabled,
      language,
      dialect,
      locationType,
      patientPhone,
      providerPhone,
      alreadyWithPatient,
      phoneJoinMode,
      callerId,
      videoCallLink,
    ],
  );
  const createSessionValidation = useMemo(
    () => validateCreateSessionDraft(createSessionDraft),
    [createSessionDraft],
  );
  const canShowSchedule = useMemo(
    () =>
      locationType === 'video' &&
      isSupportedVideoCallLink(videoCallLink) &&
      canScheduleVideoCallLink(videoCallLink),
    [locationType, videoCallLink],
  );
  const visibleErrors = hasAttemptedSubmit
    ? createSessionValidation.errors
    : {};

  const ensureValidDraft = () => {
    if (createSessionValidation.isValid) {
      return true;
    }
    setHasAttemptedSubmit(true);
    toast({
      title: 'Missing information',
      description: firstValidationMessage(createSessionValidation.errors),
      status: 'warning',
      duration: 3000,
      isClosable: true,
    });
    return false;
  };

  const {
    dateOptions,
    handleScheduleDateChange,
    handleScheduleSubmit,
    handleScheduleTimeChange,
    isScheduling,
    openScheduleModal,
    scheduledDateKey,
    scheduledTimeKey,
    userTimezoneLabel,
    visibleScheduleError,
  } = useCreateSessionSchedule({
    draft: createSessionDraft,
    ensureValidDraft,
    onDraftAccepted: () => setHasAttemptedSubmit(false),
    onScheduleClose: scheduleDisclosure.onClose,
    onScheduleOpen: scheduleDisclosure.onOpen,
    onScheduled,
    toast,
  });

  const showUnwiredToast = () => {
    toast({
      title: CREATE_SESSION_UNWIRED_TOAST_TITLE,
      status: 'info',
      duration: 2500,
      isClosable: true,
    });
  };

  const handleInterpreterToggle = (checked: any) => {
    setInterpreterEnabled(checked);
    if (checked) {
      // Re-enabling interpreter for a selected patient should fall back to
      // their language rather than reopening an empty field.
      const defaultLanguage = patientLanguageFromItem(patientItem);
      if (defaultLanguage) {
        seedLanguageSelection(
          defaultLanguage,
          patientDialectFromItem(patientItem) || '',
        );
      }
    } else {
      resetLanguageFields();
    }
  };

  const handleLocationChange = (nextLocationType: any) => {
    setLocationType(nextLocationType);
    if (nextLocationType !== 'phone') {
      // Leaving phone abandons the field; clear the manual-edit guard so a
      // later return seeds the chart number fresh.
      patientPhoneEditedRef.current = false;
      setPatientPhoneState('');
      setProviderPhone('');
    } else {
      // Seeding the chart number on entry is not a manual edit.
      setPatientPhoneState(
        current =>
          current ||
          formatCreateSessionPhoneNumber(patientPhoneFromItem(patientItem)),
      );
      // Prefill the provider's own number from their saved profile value so
      // they don't retype it every call.
      setProviderPhone(
        current =>
          current ||
          formatCreateSessionPhoneNumber(userData?.providerPhone || ''),
      );
    }
    if (nextLocationType !== 'video') {
      setVideoCallLink('');
    }
  };

  const handleStart = async () => {
    if (!ensureValidDraft()) return;
    setHasAttemptedSubmit(false);

    const launchIntent = buildCreateSessionLaunchIntent(createSessionDraft);

    switch (launchIntent.action) {
      case CREATE_SESSION_LAUNCH_ACTIONS.ROUTE: {
        // Normalize the web dial-out patient number to E.164 once, so both the
        // encounter and scribe routes hand the SIP backend a valid number.
        const routeState: Record<string, any> = {...(launchIntent.state || {})};
        if (routeState.dialOutPhoneNumber) {
          routeState.dialOutPhoneNumber = toE164(routeState.dialOutPhoneNumber);
        }

        // Simultaneous interpretation is ONLY valid for the web phone call-out
        // (provider on web + patient dialed in), which is exactly the case
        // where a dial-out number is present. In-person / already-with-patient
        // has no second leg to translate against, so force it off there.
        const effectiveSimultaneous =
          simultaneousEnabled && Boolean(routeState.dialOutPhoneNumber);

        if (launchIntent.path === ENCOUNTER_ROUTE) {
          const providerName =
            userData?.displayName || (userData as any)?.name || undefined;
          setIsStarting(true);
          try {
            const liveKitConnectionDetails = await fetchConnectionDetails({
              language: routeState.language || '',
              dialect: routeState.dialect || '',
              patientId: routeState.patientId || undefined,
              patientName: routeState.patientName || undefined,
              ehrPatientId: routeState.ehrPatientId || undefined,
              patientGender: (routeState.patient as any)?.gender || undefined,
              providerName,
              encounterId: routeState.continueEncounterId || undefined,
              workflowType: LIVE_ENCOUNTER_WORKFLOW_TYPE,
              simultaneous: effectiveSimultaneous,
              // Web dial-out: the patient is on a phone (SIP) leg, so the
              // backend stamps `is_telephonic` into room metadata and the
              // agent classifies the Conversation as a Phone visit even if
              // the client-side doc write never lands.
              isTelephonic: Boolean(routeState.dialOutPhoneNumber),
              requestAuthToken: true,
            });
            navigate(launchIntent.path!, {
              state: {
                ...routeState,
                providerName,
                simultaneous: effectiveSimultaneous,
                liveKitConnectionDetails,
              },
            });
          } catch (error) {
            toast({
              title: 'Connection failed',
              description:
                error.message || 'Unable to prepare the live encounter.',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          } finally {
            setIsStarting(false);
          }
          return;
        }
        navigate(launchIntent.path!, {state: routeState});
        return;
      }

      case CREATE_SESSION_LAUNCH_ACTIONS.CREATE_BOT: {
        const botPayload = launchIntent.payload as any;
        setIsStarting(true);
        try {
          const response = await createBot(botPayload);
          if (!response?.success) {
            throw new Error(
              response?.message || 'Unable to start the video session.',
            );
          }
          // Scheduled bots have no room/session yet — just confirm and return
          // home (the scheduled list tracks them). Join-now bots open the live
          // video view, which streams the transcript + status read-only from
          // Firestore (it never joins the LiveKit room).
          if (response.scheduled) {
            toast({
              title: 'Session scheduled',
              description: 'The assistant will join at the scheduled time.',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
            navigate('/');
            return;
          }
          if (response.botId) {
            navigate(VIDEO_SESSION_ROUTE, {
              state: {
                botId: response.botId,
                telecallId: response.success,
                platform: response.platform,
                product: response.product || botPayload.mode,
                language: botPayload.language || '',
                dialect: botPayload.dialect || '',
                patientName: botPayload.patientName || '',
                patientId: botPayload.patientId || null,
                ehrPatientId: botPayload.ehrPatientId || null,
              },
            });
            return;
          }
          toast({
            title: 'Assistant summoned',
            description: 'The assistant is joining the meeting.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          navigate('/');
        } catch (error) {
          toast({
            title: 'Error',
            description: error.message || 'Failed to start the video session.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        } finally {
          setIsStarting(false);
        }
        return;
      }

      case CREATE_SESSION_LAUNCH_ACTIONS.PHONE_CALL: {
        const phoneState: Record<string, any> = launchIntent.state || {};
        // No interpreter => silent telephonic scribe. Scribe is always
        // single-leg (it just transcribes the provider's call), so it ignores
        // the "already with patient" switch.
        const isScribe = !phoneState.interpreterEnabled;
        const singleLeg = isScribe || Boolean(phoneState.alreadyWithPatient);
        const product = isScribe ? 'scribe' : 'interpreter';
        const providerNumber = phoneState.providerPhone;
        // Verified outbound caller ID (defaults to the org's number so the
        // call to the provider rings through the clinic phone system as an
        // internal call instead of the queue). '' = default Opalite number.
        const selectedCallerId = phoneState.callerId || undefined;
        setIsStarting(true);
        try {
          const response = singleLeg
            ? await requestPhoneCall({
                phoneNumber: providerNumber,
                product,
                language: phoneState.language,
                dialect: phoneState.dialect,
                userId: userData?.userId,
                facilityId: userData?.facilityId,
                callerId: selectedCallerId,
                patientId: phoneState.patientId || undefined,
                patientName: phoneState.patientName || undefined,
                providerName:
                  userData?.displayName || (userData as any)?.name || undefined,
              })
            : await requestThreeWayCall(
                phoneState.patientPhone,
                providerNumber,
                phoneState.language,
                phoneState.dialect,
                userData?.userId || '',
                userData?.facilityId || '',
                selectedCallerId,
              );
          if (!response?.success) {
            throw new Error(
              response?.message || 'Unable to start the phone call.',
            );
          }
          // Remember the provider's number for next time.
          if (userData?.userId && providerNumber) {
            void saveProviderPhone(userData.userId, providerNumber);
          }
          const callId =
            response.callID || response.callId || response.roomName || '';
          navigate(PHONE_SESSION_ROUTE, {
            state: {
              callId,
              mode: singleLeg ? 'single-leg' : 'three-way',
              product,
              alreadyWithPatient: singleLeg,
              language: phoneState.language || '',
              dialect: phoneState.dialect || '',
              providerPhone: providerNumber || '',
              patientPhone: phoneState.patientPhone || '',
              patient: phoneState.patient || null,
              patientName: phoneState.patientName || '',
              patientId: phoneState.patientId || null,
              ehrPatientId: phoneState.ehrPatientId || null,
            },
          });
        } catch (error) {
          toast({
            title: 'Connection failed',
            description: error.message || 'Unable to start the call.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        } finally {
          setIsStarting(false);
        }
        return;
      }

      default:
        showUnwiredToast();
    }
  };

  const resetLaunchState = (nextPatientPhone = '') => {
    setHasAttemptedSubmit(false);
    setLocationType('inPerson');
    setProviderPhone('');
    setAlreadyWithPatient(false);
    setPhoneJoinMode('web');
    setSimultaneousEnabled(false);
    setCallerId('');
    setVideoCallLink('');
    patientPhoneEditedRef.current = false;
    setPatientPhoneState(formatCreateSessionPhoneNumber(nextPatientPhone));
  };

  return {
    ...languageFields,
    alreadyWithPatient,
    setAlreadyWithPatient,
    phoneJoinMode,
    setPhoneJoinMode,
    callerId,
    setCallerId,
    createSessionDraft,
    createSessionValidation,
    canShowSchedule,
    dateOptions,
    ensureValidDraft,
    handleInterpreterToggle,
    handleLocationChange,
    handleScheduleDateChange,
    handleScheduleSubmit,
    handleScheduleTimeChange,
    handleStart,
    hasAttemptedSubmit,
    interpreterEnabled,
    isScheduling,
    isStarting,
    locationType,
    openScheduleModal,
    patientPhone,
    providerPhone,
    resetLaunchState,
    scheduleDisclosure,
    scheduleModal: {
      dateOptions,
      isOpen: scheduleDisclosure.isOpen,
      isSubmitting: isScheduling,
      onClose: scheduleDisclosure.onClose,
      onDateChange: handleScheduleDateChange,
      onSubmit: handleScheduleSubmit,
      onTimeChange: handleScheduleTimeChange,
      scheduledDateKey,
      scheduledTimeKey,
      userTimezoneLabel,
      visibleScheduleError,
    },
    scheduledDateKey,
    scheduledTimeKey,
    setHasAttemptedSubmit,
    setInterpreterEnabled,
    simultaneousEnabled,
    setSimultaneousEnabled,
    setLocationType,
    setPatientPhone,
    setProviderPhone,
    setVideoCallLink,
    userTimezoneLabel,
    visibleErrors,
    visibleScheduleError,
    videoCallLink,
  };
}

export default useEncounterLauncher;
